import RichText from '@eneco/components/RichText/RichText';
import { RegisterType } from '@eneco/dc-be/client';
import { Button, Grid, Heading, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { DoubleMeter, SingleMeter } from '@eneco/sparky/illustrations';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const MeterRegistersTypeCheckStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { getValues, setValue } = useFormContext<PriceSimulationFormSchema>();

  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const onNextClick = async () => {
    const hasExclNight = getValues('hasExclusiveNightMeter');
    const meterType = getValues('meterSubTypeRegister');

    if (!hasExclNight) {
      setValue('electricityConsumption.exclusiveNight', null);
    }

    if (meterType === 'Single') {
      setValue('electricityConsumption.day', null);
      setValue('electricityConsumption.night', null);
    } else {
      setValue('electricityConsumption.single', null);
    }

    const hasMeterSelected = await trigger('meterSubTypeRegister');
    if (hasMeterSelected) {
      trackEventBE('pbt_calculation_known_tarrif', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.digitalMeterTypeStep.title.value}
        </Heading>
      </Stack>
      <Stretch>
        <Stack gap={10} direction={'column'}>
          <Controller
            control={control}
            name={'meterSubTypeRegister'}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name={'meterSubTypeRegister'}
                aria-labelledby={fields.digitalMeterTypeStep.meterTypeList.value.label}
                direction="column"
                value={value}
                error={errors.meterSubTypeRegister?.message}
                onValueChange={val => {
                  onChange(val);
                }}>
                {fields.digitalMeterTypeStep.meterTypeList.value.options.map(registerType => {
                  return (
                    <RadioTile
                      value={registerType.name}
                      key={registerType.name}
                      icon={getRegisterTypeIcon(registerType.name)}>
                      <Stack direction="column" gap="2" alignY="center">
                        <Text size="BodyS" weight="bold">
                          {registerType.label}
                        </Text>
                        <Text size="BodyS">{registerType.description}</Text>
                      </Stack>
                    </RadioTile>
                  );
                })}
              </RadioGroup>
            )}
          />
          <Grid.Item alignSelf="stretch">
            <Controller
              control={control}
              name={'hasExclusiveNightMeter'}
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  name={'hasExclusiveNightMeter'}
                  direction="row"
                  label={fields.digitalMeterTypeStep.nightMeterList.value.label}
                  value={value ? 'yes' : 'no'}
                  onValueChange={newValue => {
                    onChange(newValue === 'yes');
                  }}>
                  <StyledGrid gridTemplateColumns="1fr 1fr" gap={4}>
                    {fields.digitalMeterTypeStep.nightMeterList.value.options.map(option => (
                      <RadioTile value={option.name} key={option.name}>
                        <Text size="BodyS" weight="bold">
                          {option.label}
                        </Text>
                      </RadioTile>
                    ))}
                  </StyledGrid>
                </RadioGroup>
              )}
            />
          </Grid.Item>
          <label htmlFor={'meterSubTypeRegister'}>
            <Stack>
              <Text weight="bold">{fields.digitalMeterTypeStep.explanationTitle.value}</Text>
              <RichText html={fields.digitalMeterTypeStep.explanationDescription.value} />
            </Stack>
          </label>
        </Stack>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};

const getRegisterTypeIcon = (option: RegisterType | string) => {
  switch (option) {
    case 'Single':
      return <SingleMeter size="small" />;
    default:
      return <DoubleMeter size="small" />;
  }
};

const StyledGrid = styled(Grid, {
  width: '100%',
});
